<template>
  <teleport to="body">
    <transition name="toast" appear>
      <div v-if="visible" :class="['toast d-block', `toast--${type}`]">
        <div class="toast__content">
          <div class="toast__icon">
            <component :is="iconComponent" />
          </div>
          <div class="toast__message">
            {{ message }}
          </div>
          <button class="toast__close" @click="close" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div class="toast__progress">
          <div
            class="toast__progress-bar"
            :style="{ animationDuration: `${duration}ms` }"
          ></div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { h } from "vue";
export default {
  name: "Toast",
  props: {
    message: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: "info",
      validator: (val) => ["success", "error", "info", "warning"].includes(val),
    },
    duration: {
      type: Number,
      default: 4000,
    },
    position: {
      type: String,
      default: "top-right",
      validator: (val) =>
        ["top-right", "top-left", "bottom-right", "bottom-left"].includes(val),
    },
  },
  emits: ["closed"],
  data() {
    return {
      visible: false,
      isPaused: false,
      timerId: null,
      startTime: 0,
      remainingTime: 0,
    };
  },
  computed: {
    iconComponent() {
      const icons = {
        success: {
          render() {
            return h(
              "svg",
              { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none" },
              [
                h("path", {
                  d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                  stroke: "currentColor",
                  "stroke-width": 2,
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                }),
              ]
            );
          },
        },
        error: {
          render() {
            return h(
              "svg",
              { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none" },
              [
                h("path", {
                  d: "M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z",
                  stroke: "currentColor",
                  "stroke-width": 2,
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                }),
              ]
            );
          },
        },
        info: {
          render() {
            return h(
              "svg",
              { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none" },
              [
                h("path", {
                  d: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                  stroke: "currentColor",
                  "stroke-width": 2,
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                }),
              ]
            );
          },
        },
        warning: {
          render() {
            return h(
              "svg",
              { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none" },
              [
                h("path", {
                  d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
                  stroke: "currentColor",
                  "stroke-width": 2,
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                }),
              ]
            );
          },
        },
      };
      return icons[this.type];
    },
  },
  methods: {
    show() {
      this.visible = true;
      this.startTimer();
    },
    startTimer() {
      this.startTime = Date.now();
      this.remainingTime = this.duration;
      this.timerId = setTimeout(this.close, this.duration);
    },
    pauseTimer() {
      if (this.timerId && !this.isPaused) {
        clearTimeout(this.timerId);
        this.remainingTime = this.duration - (Date.now() - this.startTime);
        this.isPaused = true;
      }
    },
    resumeTimer() {
      if (this.isPaused) {
        this.isPaused = false;
        this.timerId = setTimeout(this.close, this.remainingTime);
      }
    },
    close() {
      this.visible = false;
      this.clearTimer();
      this.$emit("closed");
    },
    clearTimer() {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    },
  },
  mounted() {
    this.show();
  },
  unmounted() {
    this.clearTimer();
  },
};
</script>

<style scoped>
.toast {
  position: fixed;
  top: 1rem;
  right: 1rem;
  min-width: 320px;
  max-width: 480px;
  backdrop-filter: blur(16px);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 1000;
  user-select: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.toast--success {
  background: linear-gradient(
    135deg,
    rgba(16, 185, 129, 0.9) 0%,
    rgba(5, 150, 105, 0.9) 100%
  );
}

.toast--error {
  background: linear-gradient(
    135deg,
    rgba(239, 68, 68, 0.9) 0%,
    rgba(220, 38, 38, 0.9) 100%
  );
}

.toast--info {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.9) 0%,
    rgba(37, 99, 235, 0.9) 100%
  );
}

.toast--warning {
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.9) 0%,
    rgba(217, 119, 6, 0.9) 100%
  );
}

.toast__content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  color: white;
}

.toast__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
}

.toast__message {
  flex: 1;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast__close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast__close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: scale(1.1);
}

.toast__progress {
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.toast__progress-bar {
  height: 100%;
  background: rgba(255, 255, 255, 0.4);
  width: 100%;
  transform-origin: left;
  animation: progress-countdown linear forwards;
}

@keyframes progress-countdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Animations */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

/* Responsive */
@media (max-width: 480px) {
  .toast {
    left: 1rem;
    right: 1rem;
    min-width: auto;
  }

  .toast__content {
    padding: 14px 16px;
  }

  .toast__message {
    font-size: 13px;
  }
}
</style>
